import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import type { SxProps, Theme } from '@mui/material/styles'
import React from 'react'

import { borderRadius, colors, spacing } from '../KestrixDesignSystem.jsx'
import { KestrixLayout } from '../KestrixLayout.tsx'

export type KestrixDataTableSkeletonBackground =
  | 'transparent'
  | 'primary'
  | 'secondary'
  | 'paper'
  | (string & {})

export type KestrixDataTableSkeletonProps = {
  showTitle?: boolean
  columnCount?: number
  rowCount?: number
  border?: boolean
  padding?: boolean
  rounded?: boolean
  background?: KestrixDataTableSkeletonBackground
  sx?: SxProps<Theme>
  'data-testid'?: string
}

const headerSkeletonSx: SxProps<Theme> = {
  bgcolor: colors.neutral[100],
  borderRadius: borderRadius.sm
}

const cellSkeletonSx: SxProps<Theme> = {
  bgcolor: colors.neutral[200],
  borderRadius: borderRadius.sm
}

const cellWidthForColumn = (columnIndex: number, columnCount: number): string => {
  if (columnIndex === 0) return '70%'
  if (columnIndex === columnCount - 1) return '40%'
  return '60%'
}

/**
 * Loading placeholder for {@link KestrixDataTable}.
 *
 * Reuses the same {@link KestrixLayout} chrome (border, padding, rounded
 * corners, background) as the loaded data table so swapping in/out of the
 * loading state doesn't shift layout. Header and body bars use the same
 * neutral tokens as {@link KestrixMetricCardSkeleton} for consistency.
 */
export const KestrixDataTableSkeleton = ({
  showTitle = true,
  columnCount = 3,
  rowCount = 4,
  border = true,
  padding = true,
  rounded = true,
  background = 'secondary',
  sx,
  'data-testid': dataTestId = 'kestrix-data-table-skeleton'
}: KestrixDataTableSkeletonProps): React.ReactElement => {
  const gridTemplateColumns = Array.from({ length: columnCount })
    .map(() => '1fr')
    .join(' ')

  return (
    <KestrixLayout
      direction="column"
      gap={12}
      background={background}
      padding={padding}
      border={border}
      rounded={rounded}
      sx={sx as object}
      data-testid={dataTestId}
    >
      {showTitle && (
        <Skeleton
          variant="rounded"
          width={200}
          height={24}
          sx={headerSkeletonSx}
        />
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          rowGap: spacing[4],
          columnGap: spacing[2],
          paddingTop: spacing[1]
        }}
      >
        {Array.from({ length: columnCount }).map((_, columnIndex) => (
          <Skeleton
            key={`th-${columnIndex}`}
            variant="rounded"
            width="55%"
            height={14}
            sx={headerSkeletonSx}
          />
        ))}
        {Array.from({ length: rowCount }).flatMap((_, rowIndex) =>
          Array.from({ length: columnCount }).map((__, columnIndex) => (
            <Skeleton
              key={`td-${rowIndex}-${columnIndex}`}
              variant="rounded"
              width={cellWidthForColumn(columnIndex, columnCount)}
              height={18}
              sx={cellSkeletonSx}
            />
          ))
        )}
      </Box>
    </KestrixLayout>
  )
}
